/*********************
Pass 1  :  Sentence Level Intervention (SENTENCE ONLY)
*********************/
import Pass from '../Pass';
import shuffle from '../../utils/shuffle';
import { interventionSituationTypes, interventionTypes } from '../../../constants';
import getIntervention from '../../../interventions/interventionFactory';

export default class Pass1 extends Pass {
  constructor(PASS_CONFIGS) {
    super();
    this.label = "PASS_1";
    const {
      errorCountThreshold, // Num sentence level errors must be greater than threshold to trigger a pass1 intervention
      recencyWindow, // Do not do a sentence level intervention if one has occurred in the last N phrases.
      occuranceMax, // Do not do more than occuranceMax pass1 interventions in the last occuranceWindow phrases.
      occuranceWindow,
      selectionRulesByIntervention, // Interventions to rotate through
     } = PASS_CONFIGS.pass1;
     this.errorCountThreshold = errorCountThreshold;
     this.recencyWindow = recencyWindow;
     this.occuranceMax = occuranceMax;
     this.occuranceWindow = occuranceWindow;

    // Instantiate config-specified interventions, with any config-specified additional rules
    let simpleRotation = [];
    Object.entries(selectionRulesByIntervention).forEach(([interventionType,ruleData]) => {
      if(!PASS_CONFIGS.disabledInterventionTypes.includes(interventionType)) {
        super.addIntervention(getIntervention(interventionType, ruleData));// TODO, incorporate intervention "modes"
        simpleRotation.push(interventionType);
      }
    });

    this.interventionQueue = shuffle(simpleRotation); // initialize a shuffled queue of interventions
  }

  run(state) {
    super.resetForNextRun(); // reset class vars (rationaleLog, runStillValid, and selectedIntervention)
    const { // destructure aspects of state that we'll make use of
      endOfPhrase,
      interventionHistoryByPhrase = [],
      inErrorCount,
      studentAREA,
      phraseMetaData,
    } = state;

    // Run Rule-Set Invocation conditions
    this.runRuleSetInvocation(endOfPhrase, inErrorCount, interventionHistoryByPhrase, studentAREA);

    if(endOfPhrase && phraseMetaData && phraseMetaData.predictionQuestions && phraseMetaData.predictionQuestions.length){
      super.selectIntervention(interventionTypes.prediction, "SELECTION_RULE - always choose prediction if questions exist");
    }

    if(this.runStillValid) { // If pass is still valid, run Over-Use Rules
      this.runOveruseRules(interventionHistoryByPhrase);
    }

    if(this.runStillValid) {
      super.qualifyInterventions(state); // Identify valid interventions, based on state and pass configs
      this.runSelectionRule(studentAREA); // If pass is still valid, run Selection Rule
    }

    // Return selected intervention
    return super.getRunResults();
  }

  ////// Rule-Set Invocation
  // Pass1 is only applicable at end of the sentence AND sentence error count is > this.errorCountThreshold.
  runRuleSetInvocation(endOfPhrase, inErrorCount, interventionHistoryByPhrase, studentAREA) {
    if(!endOfPhrase) {
      let numInterventionsInPhrase = 0;
      let phraseIndex = interventionHistoryByPhrase.length - 1;
      if(interventionHistoryByPhrase && interventionHistoryByPhrase[phraseIndex] && interventionHistoryByPhrase[phraseIndex].length) {
        numInterventionsInPhrase = interventionHistoryByPhrase[phraseIndex].length;
      }
      // make this conditional for younger student
      if(studentAREA < 6.5) {
        if(numInterventionsInPhrase < 3) {
          super.markRunInvalid("INVOCATION_RULE_1a - young reader, not a phrase intervention and intervention count less than 3");
          this.log.passDisqualified = "young reader, not a phrase intervention and intervention count less than 3";
        }
        else if(numInterventionsInPhrase < 2) {
          let threshold = Math.random();
          if(threshold < .6) {
            super.markRunInvalid("INVOCATION_RULE_1a - young reader, not a phrase intervention and intervention count less than 2 with probability");
            this.log.passDisqualified = "young reader, not a phrase intervention and intervention count less than 2 with probability";
          }
        }
      } else if(numInterventionsInPhrase < 2) {
        super.markRunInvalid("INVOCATION_RULE_1a - not a phrase intervention and intervention count less than 2 with probability");
        this.log.passDisqualified = "not a phrase intervention and intervention count less than 2 with probability";
      }

      if(this.runStillValid){
        super.markRunInvalid("INVOCATION_RULE_1 - not a phrase intervention. pass 1 is phrase/sentence level interventions only");
        this.log.passDisqualified = "not a phrase intervention. pass 1 is phrase/sentence level interventions only";
      }
    } else if (inErrorCount <= this.errorCountThreshold) {
      super.markRunInvalid("INVOCATION_RULE_1b - error count less than errorCountThreshold: count = " + inErrorCount + ", threshold = " + this.errorCountThreshold);
      this.log.passDisqualified = `error count below threshold (errorCount: ${inErrorCount}, threshold: ${this.errorCountThreshold}`;
    }
  }

  ////// Over-Use Rules
  runOveruseRules(interventionHistoryByPhrase) {
    // Do not do a sentence level intervention if one has occurred in the last recencyThreshold phrases.
    let interventionHistoryInRecencyWindow = interventionHistoryByPhrase.slice(Math.max(interventionHistoryByPhrase.length - this.recencyWindow, 0));
    let recentCount = this.generateCountInWindow(interventionHistoryInRecencyWindow);

    if(recentCount > 0) {
      super.markRunInvalid("OVERUSE_RULE_1a - phrase intervention has occurred in last recencyThreshold phrases");
      this.log.passDisqualified = "phrase intervention recently occurred";
    } else {
      // Do not do more than N phrase-level interventions in any X phrase window
      let interventionHistoryInOccuranceWindow = interventionHistoryByPhrase.slice(Math.max(interventionHistoryByPhrase.length - this.occuranceWindow, 0));
      let occuranceCount = this.generateCountInWindow(interventionHistoryInOccuranceWindow);
      if(occuranceCount >= this.occuranceMax) {
        super.markRunInvalid("OVERUSE_RULE_2a - Do not do more than occuranceMax interventions in the last occuranceWindow sentences");
        this.log.passDisqualified = `phrase interventions limit reached (limit: ${this.occuranceMax}, phraseInterventionsCount: ${occuranceCount})`;
      }
    }
  }

  // returns the count of phrases in which a simple pass intervention occurred,
  // given an input phraseHistory (an array of "phrase" arrays, where a phrase array contains interventions)
  generateCountInWindow(interventionHistoryByPhrase) {
    return interventionHistoryByPhrase.reduce((count, interventionsInPhrase) => {
      let phraseInterventionOccurred = interventionsInPhrase.reduce((intOccurred, intervention) => {
        // Determine whether a simple rotation intervention occurred during the phrase
        if(intOccurred == 1) {
          return 1;
        }
        return this.interventionQueue.reduce((match,interventionType) => {
          if(intervention.type == interventionType) {
            return 1;
          }
          return match;
        }, 0);
      }, 0);
      // Add up the phrase level interventions
      return count + phraseInterventionOccurred;
    }, 0);
  }

  ////// Selection Rule
  // Choose intervention according to simple rotation
  runSelectionRule(studentAREA) {
    let validInterventions = super.getValidInterventions();
    let validInterventionQueue = this.interventionQueue.filter(interventionType => {
      return validInterventions.includes(interventionType);
    });
    let selection = validInterventionQueue.shift();
    this.interventionQueue = this.interventionQueue.filter(interventionType => {
      return interventionType != selection;
    });

    // X% of the time on "repeat" interventions, don't perform a selection.
    // Repeat sentence less for older readers
    let randomSkipRepeatIntervention = Math.random();
    let randomSkipRepeatInterventionThreshold = .2;
    if(studentAREA > 8) {
      randomSkipRepeatInterventionThreshold = .12;
    }
    if((selection === interventionTypes.repeatsentence || selection === interventionTypes.repeatsentenceErrorsEmphasized || selection === interventionTypes.read)
      && (randomSkipRepeatIntervention >= randomSkipRepeatInterventionThreshold)) {
      super.markRunInvalid("INVOCATION_RULE - 20% of the time, when selecting repeatsentence or read, skip to pass 2");
      this.log.passDisqualified = "20% of the time, when selecting repeatsentence or read, skip to pass 2";
    } else {
      super.selectIntervention(selection, "SELECTION_RULE_1 - Simple Rotation"); // select the intervention
    }

    this.interventionQueue.push(selection); // put the intervention back at the end of the queue
  }
}
